import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import TournamentDetails, {
  type TournamentBannerState,
  type TournamentDetailsWizardPayload,
  type TournamentLogoState,
} from "./TournamentDetails";
import LocalDivisionsAndTeamsStep, {
  type LocalDivision,
} from "./components/LocalDivisionsAndTeamsStep";
import TournamentPreviewStep from "./components/TournamentPreviewStep";
import { Api } from "../../../services/interceptor";
import { setTournamentMatches } from "../../../redux/slice/matchesUiSlice";
import { setActiveTab } from "../../../redux/slice/activeTabSlice";
import { clearTournamentDraft } from "../../../redux/slice/createTournamentSlice";
import { useAppSelector } from "../../../redux/hooks";
import { selectSelectedLeagueId } from "../../../redux/slice/selectedLeagueSlice";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import {
  appendTournamentFields,
  appendTournamentImagePart,
  buildCreateTournamentPayload,
} from "./api/tournamentPayload";
import {
  bulkDivisionUpload,
  type IBulkDivisionRequest,
} from "../../myLeagues/api/myLeaguesApi";
import type { ICreateTournamentUI } from "./types/createtournament";
import { DEFAULT_TOURNAMENT_RULES, type TournamentRulesState } from "./types/createtournament";
import type { TickerRowState } from "./tournamentsDetails/matches/tickerAutomationConstants";
import "../../myLeagues/CreateLeague.scss";

type CreateTournamentProps = {
  onTournamentCreatedSuccess?: () => void;
};

const STEPS = [
  { id: 1, label: "Tournament Details" },
  { id: 2, label: "Division & Teams" },
  { id: 3, label: "Review" },
] as const;

const TrophyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const TeamsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const PreviewIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const STEP_ICONS: Record<number, React.FC> = {
  1: TrophyIcon,
  2: TeamsIcon,
  3: PreviewIcon,
};

function extractTournamentId(data: unknown): string {
  if (!data) return "";
  if (typeof data === "string") return data.trim();
  if (typeof data !== "object") return "";
  const o = data as Record<string, unknown>;
  const candidates = [o.tournamentId, o.id, o.tournament_id];
  for (const c of candidates) {
    if (c != null && String(c).trim()) return String(c).trim();
  }
  const nested = o.data as Record<string, unknown> | undefined;
  if (nested) {
    if (nested.tournamentId != null) return String(nested.tournamentId).trim();
    if (nested.id != null) return String(nested.id).trim();
  }
  return "";
}

function buildDivisionPayload(
  divisions: LocalDivision[],
  tournamentId: string,
  leagueId: string | null,
): IBulkDivisionRequest[] {
  return divisions.map((d) => {
    const groupRequests: IBulkDivisionRequest[] = d.groups.map((g) => {
      const teamList = g.teams
        .filter((t) => t.teamName.trim())
        .map((t) => ({
          teamSnapshotId: null,
          teamId: null,
          teamName: t.teamName.trim(),
          teamCode: (t.teamCode || t.teamName.slice(0, 3)).toUpperCase().trim(),
          captainPlayerId: "",
          viceCaptainPlayerId: "",
          wkPlayerId: "",
          tournamentId,
          leagueId: leagueId ?? "",
          description: t.description.trim(),
          image:
            t.logoBase64 && t.logoFileType
              ? { base64Image: t.logoBase64, fileType: t.logoFileType }
              : null,
        }));
      return {
        tournamentId,
        divisionName: g.name.trim() || "Group",
        subDivisions: null,
        teamSnapshotPutRequestList:
          teamList.length > 0
            ? { teamSnapshotPutRequestList: teamList as never }
            : null,
      };
    });
    return {
      tournamentId,
      divisionName: d.divisionName.trim() || "Division",
      subDivisions: { divisionRequests: groupRequests },
      teamSnapshotPutRequestList: null,
    };
  });
}

const CreateTournament = ({ onTournamentCreatedSuccess }: CreateTournamentProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [details, setDetails] = useState<ICreateTournamentUI | null>(null);
  const [banner, setBanner] = useState<TournamentBannerState | null>(null);
  const [logo, setLogo] = useState<TournamentLogoState | null>(null);
  const [tickerRows, setTickerRows] = useState<TickerRowState[]>([]);
  const [tournamentRules, setTournamentRules] = useState<TournamentRulesState>(DEFAULT_TOURNAMENT_RULES);
  const [divisions, setDivisions] = useState<LocalDivision[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    dispatch(clearTournamentDraft());
    sessionStorage.removeItem("tournamentId");
    sessionStorage.removeItem("tournamentName");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStep1Next = useCallback((payload: TournamentDetailsWizardPayload) => {
    setDetails(payload.data);
    setBanner(payload.banner);
    setLogo(payload.logo);
    setTickerRows(payload.tickerRows);
    setTournamentRules(payload.tournamentRules);
    setCompletedSteps((prev) => new Set([...prev, 1]));
    setCurrentStep(2);
  }, []);

  const handleStep2Next = useCallback((divs: LocalDivision[]) => {
    setDivisions(divs);
    setCompletedSteps((prev) => new Set([...prev, 2]));
    setCurrentStep(3);
  }, []);

  const handleCreate = useCallback(async () => {
    if (!details) {
      toast.error("Tournament details are missing.");
      return;
    }
    setSubmitting(true);
    try {
      // 1. POST /tournament — multipart with FormData
      const body = buildCreateTournamentPayload(
        { ...details, tournamentRules } as unknown as Record<string, unknown>,
        selectedLeagueId,
      );
      const formData = new FormData();
      appendTournamentFields(formData, body);
      appendTournamentImagePart(formData, "bannerUri", banner);
      appendTournamentImagePart(formData, "logoUri", logo);
      const res = await Api.post(QUERY_KEY.addTournament, formData);
      const tournamentId = extractTournamentId(res?.data);
      if (!tournamentId) throw new Error("Tournament created but no id returned.");

      // 2. POST /bulkDivisionUpload
      const filledDivisions = divisions.filter(
        (d) => d.divisionName.trim() && d.groups.some((g) => g.name.trim()),
      );
      if (filledDivisions.length > 0) {
        await bulkDivisionUpload({
          divisionRequests: buildDivisionPayload(filledDivisions, tournamentId, selectedLeagueId),
        });
      }

      // 3. PUT /tournament/{id}/ticker-config — only when enabled
      const enabledTicker = tickerRows.filter((r) => r.automated);
      if (details.isTickerAutomationEnabled && enabledTicker.length > 0) {
        const graphicConfigs = enabledTicker.map((r) => ({
          graphicType: r.graphicType,
          automated: true,
          intervalType: r.intervalType,
          intervalValue: r.intervalValue,
          duration: r.intervalValue,
        }));
        await Api.put(
          `${QUERY_KEY.tournamentTickerConfig}/${tournamentId}/ticker-config`,
          { graphicConfigs },
          { headers: { "Content-Type": "application/json" } },
        );
      }

      setCompletedSteps((prev) => new Set([...prev, 3]));
      toast.success("Tournament created successfully.");

      if (selectedLeagueId) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTournamentsByLeagues, selectedLeagueId] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getLeagueDetails, selectedLeagueId] });
      }

      if (onTournamentCreatedSuccess) {
        onTournamentCreatedSuccess();
        return;
      }
      dispatch(setTournamentMatches(false));
      dispatch(setActiveTab("tournaments"));
      sessionStorage.setItem("tournamentId", tournamentId);
      sessionStorage.setItem("tournamentName", details.tournamentName);
      if (selectedLeagueId) {
        sessionStorage.setItem("leagueId", selectedLeagueId);
        navigate(`/my-leagues/${selectedLeagueId}/tournaments/${tournamentId}`);
      } else {
        navigate(`/tournamentDetails/${tournamentId}`);
      }
    } catch (err) {
      const res = (err as { response?: { data?: { message?: string; error?: string } | string } })?.response;
      const data = res?.data;
      const msg =
        typeof data === "string" ? data : data?.message ?? data?.error ?? null;
      toast.error(msg || (err instanceof Error ? err.message : "Failed to create tournament."));
    } finally {
      setSubmitting(false);
    }
  }, [
    details,
    banner,
    logo,
    tournamentRules,
    divisions,
    tickerRows,
    selectedLeagueId,
    queryClient,
    dispatch,
    navigate,
    onTournamentCreatedSuccess,
  ]);

  return (
    <div className="create-tournament-wrapper create-tournament-page--no-banner">
      <div className="create-tourmament-wrap">
        <div className="ct-header">
          <h1 className="heading-league">Create Tournament</h1>
        </div>

        <div className="create-league-stepper-wrap">
          <ol className="create-league-stepper" role="list">
            {STEPS.map((step, index) => {
              const Icon = STEP_ICONS[step.id];
              const isActive = currentStep === step.id;
              const isCompleted = completedSteps.has(step.id);
              const isReached = isActive || isCompleted;
              return (
                <li
                  key={step.id}
                  className={`create-league-step ${isActive ? "active" : ""} ${
                    isCompleted ? "completed" : ""
                  }`}
                >
                  {index > 0 && (
                    <span
                      className={`create-league-step-connector ${
                        currentStep >= step.id || completedSteps.has(step.id - 1)
                          ? "dark"
                          : ""
                      }`}
                      aria-hidden
                    />
                  )}
                  <span
                    className={`create-league-step-dot ${isReached ? "reached" : ""}`}
                  >
                    <span className="create-league-step-dot-inner">
                      <Icon />
                    </span>
                  </span>
                  <span className="create-league-step-label">{step.label}</span>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="create-league-content">
          {currentStep === 1 && (
            <TournamentDetails
              onNext={handleStep1Next}
              initialValues={details ?? undefined}
              initialBanner={banner}
              initialLogo={logo}
              initialTickerRows={tickerRows}
              initialTournamentRules={tournamentRules}
            />
          )}
          {currentStep === 2 && (
            <LocalDivisionsAndTeamsStep
              initial={divisions}
              onBack={() => setCurrentStep(1)}
              onNext={handleStep2Next}
            />
          )}
          {currentStep === 3 && details && (
            <TournamentPreviewStep
              details={details}
              banner={banner}
              logo={logo}
              divisions={divisions}
              tickerRows={tickerRows}
              onPrevious={() => setCurrentStep(2)}
              onCreate={handleCreate}
              isSubmitting={submitting}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateTournament;
